import type { Ineligibility } from "../bookingFlowMachine"
import { ScreeningAppointmentType, ScreeningProgram, useScreeningQuestions, useSubmitScreening } from "../hooks"
import { SCREENING_ANSWER_LABELS, screeningQuestionText } from "../screeningCopy"
import {
  Alert,
  Box,
  Breadcrumb,
  Breadcrumbs,
  Divider,
  ForwardIcon,
  Progress,
  Typography
} from "@carrotfertility/carotene-core"
import { Form, FormAlert, FormButton, FormRadioGroup } from "@carrotfertility/carotene-core-x"
import React from "react"

type HealthQuestionsPageProps = {
  program: ScreeningProgram
  appointmentType: ScreeningAppointmentType
  onContinue: () => void
  onDisqualified: (exclusion: Ineligibility) => void
  onReturnToProviderResults?: () => void
}

// Field values are keyed by question id -> the chosen canonical answer value (e.g. "yes").
type ScreeningFormValues = Record<string, string>

// Literal strings (no react-intl): this package is English-only — see screeningCopy.ts.
export function HealthQuestionsPage({
  program,
  appointmentType,
  onContinue,
  onDisqualified,
  onReturnToProviderResults
}: HealthQuestionsPageProps): JSX.Element {
  const questionsQuery = useScreeningQuestions(program, appointmentType)
  const submitScreening = useSubmitScreening()

  if (questionsQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  if (questionsQuery.isError || !questionsQuery.data) {
    return <Alert severity="error">Something went wrong loading these questions. Please try again.</Alert>
  }

  const questions = questionsQuery.data

  // Seed every field so the radios are controlled from first render (mirrors IntakeForm).
  const defaultValues: ScreeningFormValues = Object.fromEntries(questions.map((q) => [q.id, ""]))

  const handleSubmit = (values: ScreeningFormValues) => {
    const answers = questions.map((q) => ({ questionId: q.id, answer: values[q.id] }))
    return submitScreening.mutateAsync(
      { program, appointmentType, answers },
      {
        onSuccess: (result) => {
          if (result.eligible) {
            onContinue()
            return
          }
          onDisqualified({ exclusionMessageId: result.exclusionMessageId, emergency: result.emergency })
        }
      }
    )
  }

  return (
    <Form<ScreeningFormValues> onSubmit={handleSubmit} defaultValues={defaultValues}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.md),
          maxInlineSize: 640
        })}
      >
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Breadcrumbs>
            <Breadcrumb component="button" type="button" onClick={onReturnToProviderResults}>
              Provider results
            </Breadcrumb>
            <Breadcrumb>Consent</Breadcrumb>
            <Breadcrumb currentPage>Health questions</Breadcrumb>
          </Breadcrumbs>
          <Divider />
        </Box>

        <Typography variant="h1">A few questions</Typography>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.lg) })}
        >
          {questions.map((q, i) => (
            <React.Fragment key={q.id}>
              {/* Rule between questions (not before the first), matching the Figma. */}
              {i > 0 && <Divider />}
              <FormRadioGroup
                name={q.id}
                label={screeningQuestionText(q.questionTextId)}
                required={q.required}
                controllerProps={{ rules: { required: q.required ? "Please select an answer." : false } }}
                options={(q.options ?? []).map((value) => ({ value, label: SCREENING_ANSWER_LABELS[value] ?? value }))}
              />
            </React.Fragment>
          ))}
        </Box>

        {submitScreening.isError && (
          <FormAlert severity="error">Something went wrong saving your answers. Please try again.</FormAlert>
        )}

        <Box>
          <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />}>
            Continue
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}
